import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquare, Bell, Mail } from 'lucide-react';
interface DashboardLayoutProps {
  children: ReactNode;
  role?: 'brand' | 'organizer' | null;
  onBack?: () => void;
  onMessagesClick?: () => void;
  unreadMessages?: number;
}
export function DashboardLayout({
  children,
  role,
  onBack,
  onMessagesClick,
  unreadMessages = 0
}: DashboardLayoutProps) {
  const accentColor = role === 'organizer' ? 'text-purple-500' : 'text-cyan-500';
  const borderColor = role === 'organizer' ? 'border-purple-500/20' : 'border-cyan-500/20';
  return <div className="min-h-screen bg-slate-950 text-slate-50 font-sans selection:bg-cyan-500/30" data-id="element-162">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-md border-b border-slate-800" data-id="element-163">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-164">
          <div className="flex items-center gap-4" data-id="element-165">
            {onBack && <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" title="Back to main site" data-id="element-166">
                <ArrowLeft size={20} data-id="element-167" />
              </button>}

            <button onClick={onBack} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-id="element-168">
              <span className="text-xl font-bold tracking-tight text-white" data-id="element-169">
                Sponsr<span className={accentColor} data-id="element-170">AI</span>
              </span>
            </button>

            {role && <span className={`text-xs px-2 py-0.5 rounded-full border ${borderColor} ${role === 'organizer' ? 'text-purple-400 bg-purple-500/10' : 'text-cyan-400 bg-cyan-500/10'} uppercase tracking-wider font-medium`} data-id="element-171">
                {role === 'organizer' ? 'Organizer' : 'Brand'} Dashboard
              </span>}
          </div>

          <div className="flex items-center gap-2" data-id="element-172">
            {/* Messages Button */}
            {onMessagesClick && <button onClick={onMessagesClick} className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" title="Messages" data-id="element-173">
                <MessageSquare size={20} data-id="element-174" />
                {unreadMessages > 0 && <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${role === 'brand' ? 'bg-cyan-500 text-slate-900' : 'bg-purple-500 text-white'}`} data-id="element-175">
                    {unreadMessages}
                  </span>}
              </button>}

            {/* Notifications */}
            <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" title="Notifications" data-id="element-176">
              <Bell size={20} data-id="element-177" />
            </button>

            {/* User Avatar */}
            <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold ml-2" data-id="element-178">
              {role === 'organizer' ? 'OR' : role === 'brand' ? 'BR' : 'US'}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-179">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-180">
          {children}
        </motion.div>
      </main>

      {/* Footer with Contact */}
      <footer className="border-t border-slate-800 mt-12" data-id="element-181">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-182">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500" data-id="element-183">
            <p data-id="element-184">© 2024 SponsrAI. All rights reserved.</p>
            <a href="mailto:info@sponsrai.se" className={`flex items-center gap-2 hover:${role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} transition-colors`} data-id="element-185">
              <Mail size={16} data-id="element-186" />
              info@sponsrai.se
            </a>
          </div>
        </div>
      </footer>
    </div>;
}